import { useTranslation } from 'module/hooks/use-translation';
import { useDeleteConfirmModal } from 'module/ui/components/atoms/modal/use-delete-confirm-modal';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@rocketlane/ui';
import { tv } from 'tailwind-variants';

const headerVariants = tv({
  slots: {
    container: 'flex w-full',
    cell: 'flex items-center py-1 bg-[#f9f9fa] border-b border-[#f4f4f4]',
    text: 'font-semibold text-[12px] leading-[18px] text-[#6f6f6f] tracking-[0.16px]',
    iconButton: 'cursor-pointer text-[#6f6f6f] transition-colors',
  },
  variants: {
    cellType: {
      main: {
        cell: 'h-8 px-2',
      },
      sync: {
        cell: 'h-8 px-0',
      },
      actions: {
        cell: 'h-8 justify-center px-2',
      },
    },
  },
});

// Helper function to map nativeResourceType to display name
const getNativeResourceDisplayName = (nativeResourceType: string): string => {
  switch (nativeResourceType.toLowerCase()) {
    case 'user':
      return 'Contact'; // or 'People' if you prefer
    case 'company':
      return 'Account';
    case 'project':
      return 'Project';
    default:
      return 'Project'; // fallback
  }
};

export const Header = ({
  name,
  nativeResourceType,
  onRemove,
}: {
  name: string;
  nativeResourceType: string;
  onRemove: () => void;
}) => {
  const { container, cell, text, iconButton } = headerVariants();
  const deleteConfirmTitle = useTranslation('delete');
  const deleteConfirmMessage = useTranslation(
    'settings.integrations.sf.resource_mappings.delete',
  );

  const { open: confirmDelete, dialog: deleteConfirmationDialog } =
    useDeleteConfirmModal({
      title: deleteConfirmTitle,
      content: deleteConfirmMessage,
      onOk: () => {
        onRemove();
      },
    });
  const displayName = getNativeResourceDisplayName(nativeResourceType);

  return (
    <>
      <div className={container()}>
        <div className={cell({ cellType: 'main' })} style={{ flex: 1 }}>
          <span className={text()}>{name}</span>
        </div>
        <div className={cell({ cellType: 'sync' })} style={{ width: '32px' }}>
          {/* Sync column spacer */}
        </div>
        <div className={cell({ cellType: 'main' })} style={{ flex: 1 }}>
          <span className={text()}>{displayName}</span>
        </div>
        <div className={cell({ cellType: 'actions' })} style={{ flex: 1 }}>
          <Button
            variant="ghost"
            onClick={confirmDelete}
            rightIcon={<FontAwesomeIcon icon={faTrash} />}
            className={iconButton()}
            aria-label="Delete resource"
          ></Button>
        </div>
      </div>
      {deleteConfirmationDialog}
    </>
  );
};
